import { PsychologistHeadlineEnum } from "@mindlercare/schema";
import { TreatmentStatus } from "../../components/psychologist/specialities.types";
import { CreatePsychologistComment } from "../../components/psychologist/comment/type";
import { CreateDescriptionMessage } from "../../components/psychologist/description/type";
import { RouterOutput } from "@mindlercare/mindlerapi-client";

import { First } from "helpers/arrays";
import { z } from "zod";

export type PatientMessage = {
  body: string;
  countryId: number;
  createdAt: string;
  languageId: number;
  messageId: string;
  psychologistMessageHistoryId: number;
  receiverUserId: number;
  senderUserId: number;
  senderUser: SenderUser;
  title: string;
  updatedAt: Date;
  senderLanguageTitle: string;
  senderLanguageDescription: string;
  description: string;
  psychologistName: string;
  psychologistMessageTemplateType: string;
  viewed: boolean;
  deleted: boolean;
  attachmentKey?: string;
  attachmentKeys?: string[];
};

export type PatientMessageTemplate = {
  templateType: string;
  title: string;
  body: string;
  shortDescription: string;
  roleId: string[];
};

export type SenderUser = {
  firstName: string;
  lastName: string;
};

export type Message = {
  messageId: number;
  messageType: string;
  visible: boolean;
  title: string;
  shortMessage: string;
  description: string;
  link: string;
  linkLabel: string;
  viewed: boolean;
  createdAt: string;
  updatedAt: string;
};

type SlotApiResponse = First<RouterOutput["slots"]["psychologist"]["calendar"]>;

export interface SlotApiRes extends Omit<
  SlotApiResponse,
  "feeWaived" | "blocked" | "patientUserId"
> {
  feeWaived: boolean;
  blocked: boolean;
}

export interface Slot extends SlotApiRes {
  status: SlotStatus;
  showStatusOnHover: boolean;
  showColorStatus: boolean;
  isDurationOneHour: boolean;
  formattedSlotTitle: string;
  translatedHoverText: string;
  slotTime: string;
  psychologist?: Psychologist;
  patient?: Patient;
  userId: number | null;
  templateName?: TemplateName;
}

export type Event = {
  event: Slot;
  start: Date | number;
  end: Date | number;
};

export type Template = First<RouterOutput["slots"]["templates"]["getAll"]>;

export const TemplateNames = {
  new_visit: "new_visit",
  revisit: "revisit",
  short_visit: "short_visit",
  tech_test: "tech_test",
  couples_therapy: "couples_therapy",
  blocker: "blocker",
  discussion_with_non_mindler_caregiver:
    "discussion_with_non_mindler_caregiver",
  email_or_digital_contact: "email_or_digital_contact",
  reporting: "reporting",
  advisory_consult: "advisory_consult",
  iKBT: "iKBT",
  emdr: "emdr",
  supervision: "supervision",
  work_hours: "work_hours",
  long_visit: "long_visit",
  diagnostics_journal_entry: "diagnostics_journal_entry",
  feedback: "feedback",
  adhd_screening: "adhd_screening",
  clinical_supervision: "clinical_supervision",
  contracted_other: "contracted_other",
  live_management_30: "live_management_30",
  live_management_60: "live_management_60",
  mindler_academy: "mindler_academy",
  daily_hub: "daily_hub",
} as const;

export const templatesNameSchema = z.nativeEnum(TemplateNames);
export type TemplateName = z.infer<typeof templatesNameSchema>;

export const enum SlotStatus {
  BOOKED = "BOOKED",
  OPEN = "OPEN",
  CANCELED = "CANCELED",
  LATE_CANCELED = "LATE_CANCELED",
  NO_SHOW = "NO_SHOW",
  ORGANIZER_NO_SHOW = "ORGANIZER_NO_SHOW",
  NO_SHOW_ALL = "NO_SHOW_ALL",
  UNAVAILABLE = "UNAVAILABLE",
}

export const enum PromoCategory {
  B2B = "B2B",
  B2B_MEMBERSHIP = "B2B_MEMBERSHIP",
  BUNDLE = "BUNDLE",
  CS = "CS",
  INTERNAL = "INTERNAL",
  MARKETING = "MARKETING",
  OTHER = "OTHER",
}

export const enum SlotPaidStatus {
  NOT_PAID = 0,
  PAID = 1,
}

export type QuestionnaireRequest = {
  clinicalId: string;
  createdAt: string;
  id: string;
  requestedAt: string;
  requestingPsychologistId: string;
  requestingPsychologistName?: string;
  userId: string;
  completedAt?: string;
  psychologistSeenAt: string | null;
  automaticallySentAt: string | null;
  automaticallySentForSlotId: string | null;
  externalSource: string | null;
};

export type QuestionnaireRequestApi = {
  clinical_id: string;
  created_at: string;
  id: string;
  requested_at: string;
  requesting_psychologist_id: string;
  user_id: string;
  completed_at?: string;
  psychologist_seen_at: string | null;
  automatically_sent_at: string | null;
  automatically_sent_for_slot_id: string | null;
  external_source: string | null;
};

export type UserAnswerRequestApi = {
  answer_id: string;
  created_at: string;
  group_id: string;
  id: string;
  question_id: string;
  survey_id: string;
  request_id: string | null;
  icbt_request_id: number | null;
};

export type UserAnswer = {
  id: string;
  answerId: string;
  createdAt: string;
  groupId: string;
  questionId: string;
  surveyId: string;
  requestId: string;
};

type ComplexCell = {
  tooltip?: string;
  value: string;
  status?: string;
};
export type QuestionnaireTableRow = {
  title: ComplexCell;
  dateSent: string;
  sentBy?: string;
  score?: ComplexCell;
  dateCompleted?: string;
  userAnswer?: UserAnswer[];
  clinicalId: string;
  basicInfo?: QuestionnaireBasic;
  id: string;
  isNew: boolean;
  rowMetadata?: {
    status: "info" | "alert";
  };
};

type StatusColor = { hex?: string; name: string };

type ScoreStep = {
  stepText: string;
  stepDescription: string;
  boundary: number;
  statusColor: StatusColor;
};

export type QuestionnaireBasic = {
  clinicalId: string;
  copyright: string;
  description: string;
  name: string;
  questionnaireId: number;
  clinicalName: string;
  scoreSteps: ScoreStep[];
};

export type AnswerOption = {
  name: string;
  optionId: string;
  score: number;
  statusColor: StatusColor;
  weight: number;
};
export type QuestionnaireContent = {
  name: string;
  questionnaireId: number;
  clinicalId: string;
  clinicalName: string;
  illustration: {
    url: string;
  };
  description: string;
  copyright: string;
  topLevelQuestion: string;
  scoreSteps: {
    stepText: string;
    stepDescription: string;
    boundary: number;
    statusColor: StatusColor;
  }[];
  estimatedCompletionTime: string;
  questions: {
    questionId: string;
    name: string;
    type: string;
    userAnswer?: AnswerOption;
    optionGroup: {
      optionGroupId: string;
      title: string;
      options: AnswerOption[];
    };
  }[];
};

export type FormData = {
  type: string;
  name_clinic: string;
  address: string;
  referral_certificate: string;
};

export type User = RouterOutput["users"]["getCurrentUser"];
export type ApiPatient = RouterOutput["patients"]["getByUserId"];

type NullableNames = { firstName: string | null; lastName: string | null };
export type UserWithNullableNames = Omit<User, "firstName" | "lastName"> &
  NullableNames;

export type Psychologist = {
  psychologistId: number | null;
  gender: string;
  /**
   * @deprecated — use `headlineEnum` instead
   */
  headline: string;
  comments?: CreatePsychologistComment[];
  /**
   * @deprecated — use `descriptions` instead
   */
  summary?: string;
  descriptions: CreateDescriptionMessage[];
  thumbnail: string | null;
  bigImage: string | null;
  languages: number[];
  specialities: number[];
  slots: Slot[];
  phone: string;
  hsa: string;
  headlineEnum?: PsychologistHeadlineEnum;
  firstName?: string;
  lastName?: string;
  user?: User;
  absenceStatus?: string | null;
  employmentType?: string | null;
};

export type PsychologistProfile = Omit<
  Psychologist,
  | "slots"
  | "summary"
  | "headline"
  | "user"
  | "firstName"
  | "lastName"
  | "phone"
  | "hsa"
  | "gender"
> &
  NullableNames & {
    phone: string | null;
    gender: string | null;
    hsa: string | null;
  };

export type Patient = {
  patientId: number;
  userId: number;
  blocked: number;
  email: string;
  firstName: string;
  lastName: string;
  freeCardNumber?: string;
  freeCardExpiryDate?: string;
  lastSlotId: number;
  nationalIdentity: string;
  phoneNumber: string;
  termsAndConditions: number;
  treatmentStatus: TreatmentStatus;
  lastUsedPromocode: PromoCode;
  address?: string;
  city?: string;
  postal_code?: string;
  countryId?: number;
  date_of_birth?: string;
  user?: User;
};

export type Role = {
  roleId: number;
  name: string;
  bit: number;
};

export type NewSlot = {
  slots: string[];
  start: Date;
  end: string;
  resourceId?: unknown;
  action: string;
  box: Box;
};

type Box = {
  x: number;
  y: number;
  clientX: number;
  clientY: number;
};
export type ApiMarket = RouterOutput["application"]["markets"][0];
export type Market = {
  countryId: number;
  name: string;
  codeISO2: string;
  codeISO3: string;
  defaultLanguageId: number;
  languages: Language[];
  defaultLanguage: DefaultLanguage;
};

type DefaultLanguage = {
  languageId: number;
  name: string;
  codeISO2: string;
  codeISO3: string;
};

export type Language = {
  languageId: number;
  name: string;
  codeISO2: string;
  codeISO3: string;
};

export type PromoCode = {
  companyName: string;
  createdAt: number;
  deletedAt?: number;
  discount: number;
  expiration: number;
  individualKeyId: number;
  key: string;
  masterKeyId: number;
  masterKeyTypes: string[];
  name: string;
  quota: number;
  updatedAt: number;
  usage: number;
  sessionCount: number;
  category: string;
};

export type InsuranceInfoKey =
  RouterOutput["b2bMemberships"]["getLatestB2BMembershipKey"];
